import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionDisputeCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  RejectTransactionDisputeRequestDto,
  TransactionDisputeDto,
} from "@/features/wallet/api/dto";

/**
 * Rejette une contestation ouverte (`POST /TransactionDisputes/{id}/reject`,
 * guide v3.1) — flashpay-superadmin, flashpay-admin. Le gel est levé et le
 * montant redevient disponible pour le bénéficiaire : aucune circulation de
 * fonds, donc pas de maker-checker (décision serveur). Le bénéficiaire est
 * prévenu du déblocage, l'expéditeur de l'issue. Pour restituer les fonds,
 * utiliser la contre-passation maker-checker (`Transactions/reverse-by-reference`).
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps, scope par contestation). 422 : contestation déjà close (la clé est
 * alors oubliée pour autoriser une nouvelle tentative).
 */
export const useRejectTransactionDispute = (transactionDisputeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactionDisputes", "reject", transactionDisputeId],
    mutationFn: (body: RejectTransactionDisputeRequestDto) =>
      mutateIdempotent<TransactionDisputeDto>({
        scopeId: `wallet.transactionDisputes.reject.${transactionDisputeId}`,
        url: walletEndpoints.transactionDisputes.reject(transactionDisputeId),
        body,
      }),
    onSuccess: (dispute) =>
      invalidateAfterMutation(
        queryClient,
        walletTransactionDisputeCacheKeys(
          transactionDisputeId,
          dispute?.transactionId
        )
      ),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRejectTransactionDispute failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
